import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Button, Typography, CircularProgress, Alert, Box, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Checkbox, FormControlLabel, Stack,
  Link,
  Grid,
  FormHelperText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  getTargetFacets,
  deleteTargetFacet,
  updateTargetFacet,
  addTargetFacet
} from './data';
import TargetMetricsTable from './TargetMetricsTable';

export default function TargetFacetsAccordion() {
  const [facets, setFacets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingFacet, setEditingFacet] = useState(null);
  const [formValues, setFormValues] = useState({ name: '', active: true });
  const [saving, setSaving] = useState(false);

  // State for delete confirmation
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [facetToDelete, setFacetToDelete] = useState(null);

  const sanitizeFacets = (items = []) => {
    return (items ?? []).filter((facet) => facet && typeof facet === 'object');
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getTargetFacets();
        const { results } = data
        setFacets(sanitizeFacets(results));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenAddFacet = () => {
    setEditingFacet(null);
    setFormValues({ name: '', active: true });
    setOpenDialog(true);
  };

  const handleOpenEditFacet = (facet) => {
    setEditingFacet(facet);
    setFormValues({ id: facet.id, name: facet.name, active: facet.active });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleSaveFacet = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingFacet) {
        const resp = await updateTargetFacet(formValues);
        if (resp.status === 'success' && resp.instance) {
          setFacets((prevFacets) =>
            sanitizeFacets(prevFacets).map((f) => (f.id === resp.instance.id ? resp.instance : f))
          );
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to save facet: response did not include saved facet data.');
        } else {
          setError(resp.error);
        }
      } else {
        const resp = await addTargetFacet(formValues);
        if (resp.status === 'success' && resp.instance) {
          setFacets((prevFacets) => [...sanitizeFacets(prevFacets), resp.instance]);
          setOpenDialog(false);
        } else if (resp.status === 'success') {
          setError('Unable to add facet: response did not include saved facet data.');
        } else {
          setError(resp.error);
        }
      }
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFacet = async (facetId) => {
    const resp = await deleteTargetFacet(facetId);
    if (resp.status === 'success') {
      setFacets((prevFacets) => sanitizeFacets(prevFacets).filter((f) => f.id !== facetId));
    } else {
      setError(resp.error);
    }
  };

  const confirmDeleteFacet = (facet) => {
    setFacetToDelete(facet);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!facetToDelete) return;
    setSaving(true);
    setError('');
    try {
      await handleDeleteFacet(facetToDelete.id);
    } catch (e) {
      setError(e.response?.data ?? e.message)
    } finally {
      setSaving(false);
      setConfirmDeleteOpen(false);
      setFacetToDelete(null);
    }
  };

  const handleMetricsUpdate = (facetId, updatedMetrics) => {
    setFacets(prevFacets =>
      sanitizeFacets(prevFacets).map(f =>
        f.id === facetId ? { ...f, TargetMetrics: updatedMetrics } : f
      )
    );
  };

  if (loading) return <CircularProgress size="small" />;
  if (error && !openDialog) return <Alert severity="error">{error}</Alert>;

  // Sort facets: Active first (alpha), then inactive (alpha)
  const sortedFacets = sanitizeFacets(facets).sort((a, b) => {
    if (a.active && !b.active) return -1;
    if (!a.active && b.active) return 1;
    return (a.name ?? '').localeCompare((b.name ?? ''), undefined, { sensitivity: 'base' });
  });

  return (
    <Grid container direction='row-reverse' p={1}>
      <Grid item xs={2} sx={{ textAlign: 'center' }}>
        <Button variant="contained" size="small" onClick={handleOpenAddFacet}>Add Facet</Button>
      </Grid>
       <Grid item xs={10}>
        {sortedFacets.map(facet => (
          <Accordion key={facet.id} sx={{ mb: .5 }} false disableGutters >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Grid container justifyContent={'space-between'}>
              <Grid item xs={7}>
              <Stack direction='row' columnGap={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: facet.active ? 'text.primary' : 'text.disabled',
                    fontStyle: facet.active ? 'normal' : 'italic',
                    fontWeight: 'bold'
                  }}
                >
                  {facet.name}
                  {!facet.active && ' (Inactive)'}
                </Typography>
                <Link
                  onClick={(event) => { event.stopPropagation(); handleOpenEditFacet(facet); }}
                >
                  (edit)
                </Link>
                {(facet.TargetMetrics?.length ?? 0) === 0 && (
                  <Link
                    color="error"
                    onClick={(event) => {
                      event.stopPropagation();
                      confirmDeleteFacet(facet);
                    }}
                  >
                    (delete)
                  </Link>
                )}
              </Stack>
              </Grid>
              <Grid item xs={3} textAlign={'right'}>
              { ((activeMetrics) => <Typography variant='body2'>{
                activeMetrics.length > 0 ? `Active metrics: ${activeMetrics.map(m => m.name).join(', ')}` : ('No active metrics')
              }</Typography>)((facet.TargetMetrics ?? []).filter(m => !!m.active).sort((a,b) => a.name.localeCompare(b.name)))}
              </Grid>
            </Grid>
            </AccordionSummary>

            {/* Shaded Details Section */}
            <AccordionDetails sx={{ backgroundColor: '#fafafa', p: 2 }}>
              <TargetMetricsTable
                targetFacetId={facet.id}
                initialMetrics={facet.TargetMetrics ?? []}
                onMetricsChange={(updatedMetrics) => handleMetricsUpdate(facet.id, updatedMetrics)}
                parentFacetActive={facet.active}
              />
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Facet Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingFacet ? `Edit ${editingFacet.name}` : 'Add Facet'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {editingFacet && (
              <Stack direction="row" alignItems="center" columnGap={1}>
                <Typography variant="body2" sx={{ flexShrink: 0, width: '80px' }}>ID:</Typography>
                <TextField value={formValues.id} size="small" fullWidth disabled />
              </Stack>
            )}
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Typography variant="body2" sx={{ flexShrink: 0, width: '80px' }}>Name:</Typography>
              <TextField
                value={formValues.name}
                size="small"
                fullWidth
                onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                inputProps={{ maxLength: 50 }}
              />
              <FormHelperText>{(formValues.name || '').length}/50</FormHelperText>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formValues.active}
                  onChange={(e) => setFormValues({ ...formValues, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving} size="small">Cancel</Button>
            <Button variant="contained" onClick={handleSaveFacet} disabled={saving} size="small">
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete facet{' '}
              <strong>{facetToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              size="small"
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}